import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEqual, set } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { useDeepCompareMemo } from 'utils/useDeepCompareMemo'
import type { TCachedValuesFieldAssetData, TCachedValuesSnapshot } from './_types'
import { fetchValuesData } from './fetchValuesData'

const pollIntervalMilliseconds = 10 * 60 * 1000 // ten minutes
const makeEmptyCacheSnapshot = (): TCachedValuesSnapshot => ({ data: {}, fetchedRequestedKeys: {} })

const usePrevious = <T>(value: T, shouldStore: boolean): T | undefined => {
  const ref = useRef<T | undefined>(undefined)

  useEffect(() => {
    if (shouldStore) {
      ref.current = value
    }
  }, [value, shouldStore])

  return ref.current
}

export const useApiValuesTimeseries = ({
  args,
  preventFetch,
  watchers = [],
}: {
  args: routes.Values.Request
  preventFetch?: boolean
  watchers?: (string | number | boolean)[]
}): // TODO: would be nice if this were ResponseType probably
{
  data: TCachedValuesFieldAssetData
  loading: boolean
  error: boolean
  updateData: (pathToSet: string, dataToSet: unknown) => void
} => {
  const { dateFrom, dateTo, ...rawRest } = args
  const rest = useDeepCompareMemo(rawRest)
  const stableWatchers = useDeepCompareMemo(watchers)
  const [error, setError] = useState(false)
  /**
   * if a user is quickly clicking around, we can ensure
   * we will always show a loader if we increment and
   * decrement the number of requests that are loading,
   * assuming that a number greater than 0 means that we
   * are currently loading data
   */
  const [lastFetchedDateFrom, setLastFetchedDateFrom] = useState<string>('')
  const [lastFetchedDateTo, setLastFetchedDateTo] = useState<string>('')
  const [numberOfRequestsLoading, setNumberOfRequestsLoading] = useState(0)
  const [cachedValues, setCachedValues] = useState<TCachedValuesSnapshot>(makeEmptyCacheSnapshot)
  const [refreshTime, setRefreshTime] = useState(+new Date())
  const latestFetchIdRef = useRef(0)

  const fetchAndSetValuesData = useCallback(
    async ({ snapshot, isWipe }: { snapshot: TCachedValuesSnapshot; isWipe: boolean }) => {
      const fetchId = ++latestFetchIdRef.current

      setNumberOfRequestsLoading((rr) => rr + 1)

      try {
        const { data, updatedFetchedRequestedKeys, didFetch } = await fetchValuesData(
          args,
          snapshot.data,
          snapshot.fetchedRequestedKeys,
        )

        if (fetchId === latestFetchIdRef.current) {
          if (didFetch || isWipe) {
            setCachedValues({ data, fetchedRequestedKeys: updatedFetchedRequestedKeys })

            setLastFetchedDateFrom(dateFrom)

            setLastFetchedDateTo(dateTo)
          }

          setError(false)
        }
      } catch (err) {
        // TODO: errorLogger

        if (fetchId === latestFetchIdRef.current) {
          setError(true)
        }
      } finally {
        setNumberOfRequestsLoading((rr) => rr - 1)
      }
    },
    [args],
  )

  useEffect(() => {
    const intervalId = setInterval(() => {
      const shouldPoll = !document.hidden && document.visibilityState === 'visible'

      if (shouldPoll) setRefreshTime(+new Date())
    }, pollIntervalMilliseconds)

    return () => clearInterval(intervalId)
  }, [])

  /**
   * we want to maintain a list of things to compare to
   * to see if we should trigger a refetch of the data.
   * If we find anything has changed, we will want
   * to refresh the data
   */
  const thingsToTriggerAFetch = useMemo(
    () => ({ dateFrom, dateTo, rest, watchers: stableWatchers, refreshTime, preventFetch }),
    [dateFrom, dateTo, rest, stableWatchers, refreshTime, preventFetch],
  )

  const previousThingsToTriggerAFetch = usePrevious(thingsToTriggerAFetch, !preventFetch)

  useEffect(() => {
    /**
     * TODO: in the detailsPanel, we want to set the compareSeasonsInterval
     * to 0 upon date change, but this state update gets pretty buggy
     * unless we set it at the same time as a date change. This means that
     * everywhere we do a date change we also need to set the
     * compareSeasonsInterval to 0. This check isn't quite right
     */
    // TODO: if our args change, then preventFetch changes from true to false, we won't refetch
    if (isEqual(thingsToTriggerAFetch, previousThingsToTriggerAFetch) || preventFetch) {
      return
    }

    const existingSizeOfCachedDataInBytes = new Blob([JSON.stringify(cachedValues.data)], {
      type: 'application/json',
    }).size

    const shouldResetBecauseDatesHaveChanged = !isEqual(
      { dateFrom, dateTo },
      { dateFrom: lastFetchedDateFrom, dateTo: lastFetchedDateTo },
    )

    const shouldResetBecauseCacheLimitIsExceeded = existingSizeOfCachedDataInBytes > 1024 * 1024 * 10 // 10 MB
    const isWipe = shouldResetBecauseDatesHaveChanged || shouldResetBecauseCacheLimitIsExceeded

    let snapshot = cachedValues

    if (isWipe) {
      snapshot = makeEmptyCacheSnapshot()
    } else if (!isEqual(stableWatchers, previousThingsToTriggerAFetch?.watchers)) {
      snapshot = { data: cachedValues.data, fetchedRequestedKeys: {} }

      setCachedValues(snapshot)
    }

    fetchAndSetValuesData({ snapshot, isWipe })
  }, [thingsToTriggerAFetch])

  const updateData = (pathToSet: string, dataToSet: unknown) => {
    setCachedValues((prev) => ({ ...prev, data: set(prev.data, pathToSet, dataToSet) }))

    detailsPanelStore.setState((prev) => ({ ...prev, keyForRedrawing: new Date().toISOString() }))
  }

  return {
    data: cachedValues.data,
    loading: numberOfRequestsLoading > 0,
    error,
    updateData,
  }
}
